import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Users, AlertTriangle, Plus, History, ExternalLink, Share2 } from "lucide-react";
import GalleriStrip from "@/components/GalleriStrip";
import { usePageMeta } from "@/utils/usePageMeta";
import { formatEventDateRange, formatEventTimeRange } from "@/utils/eventDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export default function EvenemangSida() {
  const [kommande, setKommande] = useState([]);
  const [historik, setHistorik] = useState([]);
  const [visaHistorik, setVisaHistorik] = useState(false);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Evenemang i bygden – marknader, möten och konserter | vidalv.live",
    description: "Kalender över evenemang i Backe, Rossön och bygderna vid Fjällsjöälven. Marknader, möten, konserter, idrott och kultur.",
    path: "/evenemang",
  });

  useEffect(() => {
    document.title = "Evenemang – vidalv.live";
    Promise.all([
      axios.get(`${API}/evenemang`).catch(() => ({ data: [] })),
      axios.get(`${API}/evenemang/historik`).catch(() => ({ data: [] })),
    ]).then(([k, h]) => {
      setKommande(k.data || []);
      setHistorik(h.data || []);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14"><p className="text-muted-foreground">Laddar...</p></div>;
  }

  return (
    <div data-testid="evenemang-sida" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Vad händer i bygden</p>
      <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-3">Evenemang</h1>
      <p className="text-sm text-bark mb-6 leading-relaxed">
        Marknader, möten, konserter och annat som händer i Backe, Rossön och området omkring.
        Tipsa gärna redaktionen om något du vet om — vi lägger gärna till.
      </p>

      <div className="flex flex-wrap gap-3 mb-8">
        <Link
          to="/tipsa"
          data-testid="cta-tipsa-facebook"
          className="inline-flex items-center gap-2 bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors no-underline"
        >
          <Share2 size={16} /> Såg du något på Facebook? Tipsa oss
        </Link>
        <Link
          to="/skicka-evenemang"
          data-testid="cta-tipsa-evenemang"
          className="inline-flex items-center gap-2 border border-pine text-pine hover:bg-bone-alt font-body font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors no-underline"
        >
          <Plus size={16} /> Tipsa om evenemang
        </Link>
        <Link
          to="/veckans-handelser"
          data-testid="cta-veckans"
          className="inline-flex items-center gap-2 border border-pine text-pine hover:bg-bone-alt font-body font-semibold text-sm px-5 py-2.5 rounded-sm transition-colors no-underline"
        >
          <Calendar size={16} /> Veckans händelser
        </Link>
      </div>

      {/* Kommande evenemang */}
      <section className="mb-10">
        <h2 className="font-heading text-xl font-semibold text-ink mb-4">Kommande evenemang</h2>
        {kommande.length === 0 ? (
          <div data-testid="kommande-empty" className="bg-bone-alt border border-warm-border rounded-sm p-6">
            <Calendar className="text-pine mb-3" size={28} />
            <p className="text-ink mb-2 font-semibold">Inga publicerade evenemang just nu.</p>
            <p className="text-sm text-bark">Vet du om något som händer i bygden? <Link to="/tipsa" className="text-falu underline">Tipsa oss</Link>.</p>
          </div>
        ) : (
          <ul data-testid="kommande-list" className="flex flex-col gap-4">
            {kommande.map((ev) => (
              <li key={ev.id}>
                <Link
                  to={`/evenemang/${ev.id}`}
                  data-testid={`evenemang-item-${ev.id}`}
                  className="block bg-parchment border border-warm-border rounded-sm p-5 sm:p-6 no-underline hover:border-bark/40 transition-all group"
                >
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-2">
                    <h3 className="font-heading text-lg sm:text-xl text-ink group-hover:text-falu transition-colors">{ev.titel}</h3>
                    <span className="text-xs text-bark/70 uppercase tracking-wide whitespace-nowrap">
                      {formatEventDateRange(ev.datum, ev.datum_slut)}{formatEventTimeRange(ev.tid, ev.tid_slut) ? `, ${formatEventTimeRange(ev.tid, ev.tid_slut)}` : ""}
                    </span>
                  </div>
                  {(ev.kortbeskrivning || ev.beskrivning) && (
                    <p className="text-sm text-ink/80 mb-2 leading-relaxed line-clamp-3">{ev.kortbeskrivning || ev.beskrivning}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-bark">
                    {ev.plats && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {ev.plats}</span>}
                    {ev.arrangor && <span className="inline-flex items-center gap-1"><Users size={12} /> {ev.arrangor}</span>}
                  </div>
                  {ev.auto_imported && (ev.source_name || ev.source_url) && (
                    <div className="mt-2 pt-2 border-t border-warm-border/60 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-bark/70">
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink size={11} />
                        Källa:{" "}
                        {ev.source_url ? (
                          <a href={ev.source_url} target="_blank" rel="noopener noreferrer" className="text-falu hover:underline" onClick={(e) => e.stopPropagation()}>
                            {ev.source_name || ev.source_url}
                          </a>
                        ) : (
                          <span>{ev.source_name}</span>
                        )}
                      </span>
                      {ev.last_checked_at && (
                        <span>· Senast kontrollerad: {new Date(ev.last_checked_at).toLocaleDateString("sv-SE")}</span>
                      )}
                    </div>
                  )}
                  {ev.galleri && ev.galleri.length > 0 && (
                    <div className="mt-3">
                      <GalleriStrip items={ev.galleri.slice(0, 3)} compact />
                    </div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}

        {kommande.length > 0 && (
          <div data-testid="evenemang-trust-notice" className="mt-6 bg-bone-alt/60 border-l-3 border-falu/60 rounded-sm p-4 flex gap-3 items-start">
            <AlertTriangle size={16} className="text-falu shrink-0 mt-0.5" />
            <p className="text-xs text-bark leading-relaxed">
              <strong>Uppgifter kan ändras.</strong> Kontrollera alltid tid, plats och kontaktuppgifter hos arrangören innan du åker.
            </p>
          </div>
        )}
      </section>

      {/* Historik */}
      {historik.length > 0 && (
        <section data-testid="historik-section">
          <button
            type="button"
            onClick={() => setVisaHistorik((v) => !v)}
            data-testid="toggle-historik"
            className="inline-flex items-center gap-2 text-sm font-semibold text-pine hover:text-pine-hover mb-3"
          >
            <History size={14} />
            {visaHistorik ? "Dölj tidigare evenemang" : `Visa tidigare evenemang (${historik.length})`}
          </button>
          {visaHistorik && (
            <ul className="flex flex-col gap-2" data-testid="historik-list">
              {historik.slice(0, 30).map((ev) => (
                <li key={ev.id}>
                  <Link
                    to={`/evenemang/${ev.id}`}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 bg-white border border-warm-border rounded-sm px-4 py-2.5 no-underline hover:border-bark/40 transition-all"
                  >
                    <span className="font-heading text-sm text-ink/80">{ev.titel}</span>
                    <span className="text-xs text-bark/60 whitespace-nowrap">{formatEventDateRange(ev.datum, ev.datum_slut)}{ev.plats ? ` · ${ev.plats}` : ""}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
